
import React, { Fragment } from 'react';
import { useIntl } from 'react-intl';

// Components

import SummaryItem from '../SummaryItem';

// Utils

import { MM_SLASH_DD_SLASH_YYYY } from '@utils/dates/dateFormats.utils';
import { toUTCAndFormat } from '@utils/dates/date.utils';

// Constants

import { NO_PRIOR_DATA } from '@constants/ssap/miscellaneous/miscellaneous';
import { OTHER_PROGRAMS_LIST, OTHER_PROGRAMS_TYPES } from '@constants/ssap/hhmTypes/otherProgramsList';


const CurrentCoverageInfo = props => {
    const {
        applyingForCoverageIndicator,
        exchangeAndProgramNames,
        healthCoverage: {
            currentlyHasHealthInsuranceIndicator,
            currentOtherInsurance: {
                otherStateOrFederalPrograms
            },
            medicareInsurance: {
                enrolled = null,
                startDate = null,
                marketplaceCoverage = 'UNSPECIFIED'
            } = {},
            otherInsurance: {
                endDate,
                insuranceName,
                policyNumber,
                limitedBenefit
            }
        },
        idx,
        isState,
    } = props;

    const intlApi = useIntl();

    const hasOtherCoverage = programs =>
        Array.isArray(programs) && programs.some(
            p => p.eligible && p.type === OTHER_PROGRAMS_TYPES.OTHER_COVERAGE
        );

    if(!applyingForCoverageIndicator){
        return null;
    }

    const getPrograms = programs => {
        if (!programs || !programs.length) {
            return NO_PRIOR_DATA;
        }

        return programs
            .reduce(
                (eligiblePrograms, program) => {
                    const {
                        eligible,
                        type
                    } = program;

                    if(eligible) {
                        return [
                            ...eligiblePrograms,
                            intlApi.formatMessage(
                                {
                                    id: OTHER_PROGRAMS_LIST
                                        .find(p => p.type === type)
                                        .nameId
                                },
                                isState.ID ? exchangeAndProgramNames : {}
                            )
                        ];
                    } else {
                        return eligiblePrograms;
                    }
                },
                [ ]
            )
            .join('; ');
    };

    const getDateFormatted = value => {
        return value === null
            ? NO_PRIOR_DATA
            : toUTCAndFormat(value, MM_SLASH_DD_SLASH_YYYY);
    };

    // HIX-227627: startDate may be null from the AT payload
    const getMedicareStartDate = (value, isCurrentData = false) => {
        if (!value) {
            return isCurrentData ? 'common.unknown' : NO_PRIOR_DATA;
        }
        return toUTCAndFormat(value, MM_SLASH_DD_SLASH_YYYY);
    };

    const getOtherCoverageLimitedType = (limitedBenefit, otherStateOrFederalProgramsList) => {
        if (!hasOtherCoverage(otherStateOrFederalProgramsList)) {
            return null; // For previous data
        }
        return limitedBenefit ? 'ssap.summary.limited_benefit_coverage' : 'ssap.summary.full_benefit_coverage';
    };

    const getMarketplaceCoverage = (marketplaceCoverage) => {
        return (!marketplaceCoverage || marketplaceCoverage === 'UNSPECIFIED')
            ? NO_PRIOR_DATA
            : `ssap.additional_info.summary.${marketplaceCoverage}`;
    };

    return (
        <Fragment>
            <SummaryItem
                category='ssap.additional_info.summary.medicare_enrolled'
                value={enrolled}
                noPriorDataValues={[null]}
                desc={
                    [
                        [
                            `taxHousehold.householdMember[${idx}].healthCoverage.medicareInsurance.enrolled`
                        ]
                    ]
                }
            />

            {enrolled &&
                <Fragment>
                    <SummaryItem
                        category='ssap.additional_info.summary.medicare_start_date'
                        value={getMedicareStartDate(startDate, true)}
                        refineFunctionForCurrentValue={value => getMedicareStartDate(value, true)}
                        desc={
                            [
                                [
                                    `taxHousehold.householdMember[${idx}].healthCoverage.medicareInsurance.startDate`,
                                    getMedicareStartDate
                                ]
                            ]
                        }
                    />
                    {(marketplaceCoverage && marketplaceCoverage !== 'UNSPECIFIED') &&
                        <SummaryItem
                            category='ssap.additional_info.summary.medicare_marketplace_coverage'
                            replacements={exchangeAndProgramNames}
                            value={getMarketplaceCoverage(marketplaceCoverage)}
                            desc={
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].healthCoverage.medicareInsurance.marketplaceCoverage`,
                                        getMarketplaceCoverage
                                    ]
                                ]
                            }
                        />
                    }
                </Fragment>
            }

            <SummaryItem
                category = 'ssap.additional_info.summary.current_coverage'
                value = { currentlyHasHealthInsuranceIndicator }
                noPriorDataValues = { [ null ] }
                desc = {
                    [
                        [
                            `taxHousehold.householdMember[${ idx }].healthCoverage.currentlyHasHealthInsuranceIndicator`
                        ]
                    ]
                }
            />

            {
                currentlyHasHealthInsuranceIndicator &&

                <Fragment>
                    <SummaryItem
                        textCode = 'common.coverage_type'
                        value = { getPrograms(otherStateOrFederalPrograms) }
                        ignoreIntl
                        desc = {
                            [
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].healthCoverage.currentOtherInsurance.otherStateOrFederalPrograms`
                                    ],
                                    getPrograms
                                ]
                            ]
                        }
                    />

                    {hasOtherCoverage(otherStateOrFederalPrograms) &&
                        <Fragment>
                            {
                                insuranceName &&
                                <SummaryItem
                                    textCode = 'common.insurance_name'
                                    value = { insuranceName }
                                    ignoreIntl
                                    noPriorDataValues = { [ null, '' ] }
                                    desc = {
                                        [
                                            [
                                                `taxHousehold.householdMember[${ idx }].healthCoverage.otherInsurance.insuranceName`
                                            ]
                                        ]
                                    }
                                />
                            }
                            {
                                policyNumber &&
                                <SummaryItem
                                    textCode = 'common.policy_number'
                                    value = { policyNumber }
                                    ignoreIntl
                                    noPriorDataValues = { [ null, '' ] }
                                    desc = {
                                        [
                                            [
                                                `taxHousehold.householdMember[${ idx }].healthCoverage.otherInsurance.policyNumber`
                                            ]
                                        ]
                                    }
                                />
                            }

                            <SummaryItem
                                textCode='ssap.summary.other_coverage_type'
                                value={getOtherCoverageLimitedType(limitedBenefit, otherStateOrFederalPrograms)}
                                noPriorDataValues = { [ null ] }
                                desc = {
                                    [
                                        [
                                            [
                                                `taxHousehold.householdMember[${idx}].healthCoverage.otherInsurance.limitedBenefit`,
                                                `taxHousehold.householdMember[${idx}].healthCoverage.currentOtherInsurance.otherStateOrFederalPrograms`
                                            ],
                                            getOtherCoverageLimitedType
                                        ]
                                    ]
                                }
                            />

                            {
                                isState.MN && endDate !== null &&
                                <SummaryItem
                                    textCode = 'ssap.input.label.insurance_end_date'
                                    value = { getDateFormatted(endDate) }
                                    desc = {
                                        [
                                            [
                                                `taxHousehold.householdMember[${idx}].healthCoverage.otherInsurance.endDate`,
                                                getDateFormatted
                                            ]
                                        ]
                                    }
                                />
                            }
                        </Fragment>
                    }
                </Fragment>
            }
        </Fragment>
    );
};

export default CurrentCoverageInfo;
